import { useState, useEffect } from "react";
import api, { getConfig, saveConfig, testConfig, getSchedule, saveSchedule, runScheduleNow, getAppSettings, saveAppSettings } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import TestAlunnoModal from "@/components/TestAlunnoModal";

const DAYS = [
  { key: "mon", label: "Lun" },
  { key: "tue", label: "Mar" },
  { key: "wed", label: "Mer" },
  { key: "thu", label: "Gio" },
  { key: "fri", label: "Ven" },
  { key: "sat", label: "Sab" },
  { key: "sun", label: "Dom" },
];

const fmtDateTime = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function ImpostazioniPage() {
  const { refreshAnnoCorrente, annoCorrente } = useAuth();
  const [activeTab, setActiveTab] = useState("anno");
  const [testAlunnoOpen, setTestAlunnoOpen] = useState(false);
  const [config, setConfig] = useState({ platform_code: "", base_url: "https://scuolaonline.soluzione-web.it", username: "", password: "", secret_key: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  // Anno scolastico corrente
  const [annoApp, setAnnoApp] = useState(null);
  const [annoAppSaving, setAnnoAppSaving] = useState(false);
  const [annoAppMsg, setAnnoAppMsg] = useState(null);
  // Anno ISEE gestito (parametro globale, indipendente dall'anno scolastico)
  const [annoIseeGestito, setAnnoIseeGestito] = useState(null);
  const [annoIseeSaving, setAnnoIseeSaving] = useState(false);
  const [annoIseeMsg, setAnnoIseeMsg] = useState(null);

  // Anno prenotazione libri (anno scolastico per cui si raccolgono acconti libri)
  const [annoLibri, setAnnoLibri] = useState(null);
  const [annoLibriSaving, setAnnoLibriSaving] = useState(false);
  const [annoLibriMsg, setAnnoLibriMsg] = useState(null);

  // Schedule
  const [schedule, setSchedule] = useState({ enabled: false, days: ["mon", "tue", "wed", "thu", "fri"], start_time: "08:00", end_time: "18:00", interval_minutes: 15, anno_da: null, anno_a: null });
  const [scheduleMeta, setScheduleMeta] = useState({ next_run_at: null, last_run_at: null, last_run_ok: null, recent_runs: [] });
  const [schedSaving, setSchedSaving] = useState(false);
  const [schedMsg, setSchedMsg] = useState(null);

  // Export/Import configurazione Cassa (PDC, tabelle, gruppi)
  const [cfgImportFile, setCfgImportFile] = useState(null);
  const [cfgImportMode, setCfgImportMode] = useState("replace");
  const [cfgImporting, setCfgImporting] = useState(false);
  const [cfgImportResult, setCfgImportResult] = useState(null);
  const [cfgExporting, setCfgExporting] = useState(false);
  const [cfgMsg, setCfgMsg] = useState(null);
  // Sync status per anno
  const [syncByAnno, setSyncByAnno] = useState([]);
  // Sync manuale per intervallo anni (default = anno corrente)
  const _nowYear = new Date().getFullYear();
  const [syncDa, setSyncDa] = useState(String(_nowYear - 1));
  const [syncA, setSyncA] = useState(String(_nowYear));
  const loadSyncByAnno = async () => {
    try {
      const r = await api.get("/schedule/status-by-anno");
      setSyncByAnno(r.data?.anni || []);
    } catch { setSyncByAnno([]); }
  };
  useEffect(() => {
    if (activeTab !== "sync") return;
    loadSyncByAnno();
    getSchedule().then(r => {
      const s = r.data || {};
      setScheduleMeta({
        next_run_at: s.next_run_at,
        last_run_at: s.last_run_at,
        last_run_ok: s.last_run_ok,
        recent_runs: s.recent_runs || [],
      });
    }).catch(() => { });
  }, [activeTab]);

  // Backup
  const [backups, setBackups] = useState([]);
  const [backupCfg, setBackupCfg] = useState(null);
  const [backupNextRun, setBackupNextRun] = useState(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupMsg, setBackupMsg] = useState(null);

  const loadBackups = async () => {
    try {
      const r = await api.get("/admin/backups");
      setBackups(r.data.data || []);
      setBackupCfg(r.data.config || null);
      setBackupNextRun(r.data.next_run || null);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { loadBackups(); }, []);

  const runBackupNow = async () => {
    setBackupBusy(true);
    setBackupMsg({ ok: true, text: "⏳ Backup avviato — in corso… (può richiedere qualche minuto su database grandi)" });
    try {
      await api.post("/admin/backups/run");
      // Polling stato: il backup gira in background per non andare in timeout sul proxy
      const poll = async () => {
        try {
          const s = await api.get("/admin/backups/status");
          const st = s.data || {};
          if (st.running) {
            setTimeout(poll, 3000);
            return;
          }
          if (st.last_error) {
            setBackupMsg({ ok: false, text: `Errore backup: ${st.last_error}` });
          } else if (st.last_result) {
            const kb = st.last_result.size ? (st.last_result.size / 1024).toFixed(0) : "?";
            setBackupMsg({ ok: true, text: `✔ Backup creato: ${st.last_result.filename} (${kb} KB)` });
          } else {
            setBackupMsg({ ok: true, text: "✔ Backup completato" });
          }
          await loadBackups();
          setBackupBusy(false);
        } catch (e) {
          setBackupMsg({ ok: false, text: "Errore nel controllo stato backup" });
          setBackupBusy(false);
        }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      setBackupMsg({ ok: false, text: e.response?.data?.detail || "Errore backup" });
      setBackupBusy(false);
    }
  };

  const saveBackupCfg = async () => {
    setBackupBusy(true); setBackupMsg(null);
    try {
      await api.put("/admin/backups/config", backupCfg);
      setBackupMsg({ ok: true, text: "Pianificazione backup salvata" });
      await loadBackups();
    } catch (e) {
      setBackupMsg({ ok: false, text: e.response?.data?.detail || "Errore" });
    } finally { setBackupBusy(false); }
  };

  const downloadBackup = (chiave) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    window.open(`${base}/api/admin/backups/${chiave}/download`, "_blank");
  };

  const deleteBackup = async (chiave) => {
    try {
      await api.delete(`/admin/backups/${chiave}`);
      await loadBackups();
    } catch (e) {
      setBackupMsg({ ok: false, text: e.response?.data?.detail || "Errore" });
    }
  };

  const restorePrimaNota = async (chiave) => {
    const conf = window.prompt(
      `⚠️ RIPRISTINO PRIMA NOTA dal backup ${chiave}\n\n` +
      `Verranno SOSTITUITE completamente le collezioni:\n` +
      `  • pn_fatture (testate fatture, note credito, operazioni extra)\n` +
      `  • pn_costi_contabili (righe contabili collegate)\n\n` +
      `Le altre collezioni (anagrafica, piano dei conti, mensa, ecc.) NON verranno toccate.\n\n` +
      `Per confermare, digita esattamente: RESTORE`
    );
    if (conf !== "RESTORE") { if (conf !== null) alert("Operazione annullata."); return; }
    setBackupBusy(true); setBackupMsg(null);
    try {
      const fd = new FormData();
      fd.append("chiave", chiave);
      fd.append("collections", "pn_fatture,pn_costi_contabili");
      fd.append("confirm", "RESTORE");
      const r = await api.post("/admin/backups/restore-partial", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const counts = r.data?.result || {};
      const lines = Object.entries(counts).map(([k, v]) => `• ${k}: ${v} record`).join("\n");
      const notFound = (r.data?.not_found || []).length > 0 ? `\n\nNON trovate nel backup:\n${(r.data.not_found || []).map(n => `• ${n}`).join("\n")}` : "";
      setBackupMsg({ ok: true, text: `✓ Ripristino completato:\n${lines}${notFound}` });
    } catch (e) {
      setBackupMsg({ ok: false, text: e.response?.data?.detail || "Errore restore" });
    } finally { setBackupBusy(false); }
  };

  const restoreAnagraficaFornitori = async (chiave) => {
    const conf = window.prompt(
      `⚠️ RIPRISTINO ANAGRAFICA FORNITORI dal backup ${chiave}\n\n` +
      `Verrà SOSTITUITA completamente la collezione:\n` +
      `  • pn_anagrafica (tutti i fornitori)\n\n` +
      `Le altre collezioni (fatture, piano dei conti, mensa, ecc.) NON verranno toccate.\n` +
      `Eventuali fornitori aggiunti DOPO il backup andranno persi.\n\n` +
      `Per confermare, digita esattamente: RESTORE`
    );
    if (conf !== "RESTORE") { if (conf !== null) alert("Operazione annullata."); return; }
    setBackupBusy(true); setBackupMsg(null);
    try {
      const fd = new FormData();
      fd.append("chiave", chiave);
      fd.append("collections", "pn_anagrafica");
      fd.append("confirm", "RESTORE");
      const r = await api.post("/admin/backups/restore-partial", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const counts = r.data?.result || {};
      const lines = Object.entries(counts).map(([k, v]) => `• ${k}: ${v} record`).join("\n");
      setBackupMsg({ ok: true, text: `✓ Anagrafica fornitori ripristinata:\n${lines}` });
    } catch (e) {
      setBackupMsg({ ok: false, text: e.response?.data?.detail || "Errore restore" });
    } finally { setBackupBusy(false); }
  };

  const restoreCompleto = async (chiave) => {
    const conf = window.prompt(
      `🚨🚨🚨 RIPRISTINO COMPLETO 🚨🚨🚨\n\n` +
      `Verrà SOSTITUITO INTEGRALMENTE tutto il database dal backup ${chiave}.\n` +
      `TUTTE le modifiche fatte dopo il backup andranno PERSE per sempre.\n\n` +
      `Questo include:\n` +
      `  • Tutte le anagrafiche (alunni, genitori, docenti, fornitori, classi)\n` +
      `  • Tutte le fatture, movimenti, prima nota\n` +
      `  • Tutte le configurazioni\n` +
      `  • Tutte le agevolazioni, mensa, comunicator\n\n` +
      `Per confermare definitivamente, digita esattamente: RESTORE`
    );
    if (conf !== "RESTORE") { if (conf !== null) alert("Operazione annullata."); return; }
    setBackupBusy(true); setBackupMsg(null);
    try {
      const fd = new FormData();
      fd.append("chiave", chiave);
      fd.append("confirm", "RESTORE");
      const r = await api.post("/admin/backups/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const colls = r.data?.result?.collections || {};
      const lines = Object.entries(colls).map(([k, v]) => `• ${k}: ${v} record`).join("\n");
      setBackupMsg({ ok: true, text: `✓ Ripristino COMPLETO eseguito:\n${lines}` });
    } catch (e) {
      setBackupMsg({ ok: false, text: e.response?.data?.detail || "Errore restore completo" });
    } finally { setBackupBusy(false); }
  };

  const [selectiveOpen, setSelectiveOpen] = useState(null); // {chiave, collections: [{name,count,selected}]}
  const [selectiveBusy, setSelectiveBusy] = useState(false);

  const openSelectiveRestore = async (chiave) => {
    setSelectiveBusy(true);
    setBackupMsg(null);
    try {
      const r = await api.get(`/admin/backups/${chiave}/contents`);
      setSelectiveOpen({
        chiave,
        collections: (r.data.collections || []).map((c) => ({ ...c, selected: false })),
      });
    } catch (e) {
      setBackupMsg({ ok: false, text: e.response?.data?.detail || "Errore lettura contenuto backup" });
    } finally { setSelectiveBusy(false); }
  };

  const executeSelectiveRestore = async () => {
    if (!selectiveOpen) return;
    const selected = selectiveOpen.collections.filter((c) => c.selected).map((c) => c.name);
    if (selected.length === 0) { alert("Seleziona almeno una collezione da ripristinare."); return; }
    const conf = window.prompt(
      `⚠️ RIPRISTINO SELETTIVO\n\n` +
      `Verranno SOSTITUITE le seguenti collezioni dal backup:\n` +
      selected.map((c) => `  • ${c}`).join("\n") + `\n\n` +
      `Le altre NON verranno toccate.\n\n` +
      `Per confermare, digita esattamente: RESTORE`
    );
    if (conf !== "RESTORE") { if (conf !== null) alert("Operazione annullata."); return; }
    setBackupBusy(true);
    try {
      const fd = new FormData();
      fd.append("chiave", selectiveOpen.chiave);
      fd.append("collections", selected.join(","));
      fd.append("confirm", "RESTORE");
      const r = await api.post("/admin/backups/restore-partial", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const counts = r.data?.result || {};
      const lines = Object.entries(counts).map(([k, v]) => `• ${k}: ${v} record`).join("\n");
      setBackupMsg({ ok: true, text: `✓ Ripristino selettivo completato:\n${lines}` });
      setSelectiveOpen(null);
    } catch (e) {
      setBackupMsg({ ok: false, text: e.response?.data?.detail || "Errore restore" });
    } finally { setBackupBusy(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await getConfig();
        const d = res.data;
        setConfig({
          platform_code: d.platform_code || "",
          base_url: d.base_url || "https://scuolaonline.soluzione-web.it",
          username: d.username || "",
          password: d.password === "••••••••" ? "" : (d.password || ""),
          secret_key: d.secret_key === "••••••••" ? "" : (d.secret_key || ""),
        });
      } catch (e) { /* ignore */ }
      try {
        const r = await getSchedule();
        const s = r.data || {};
        setSchedule({
          enabled: !!s.enabled,
          days: s.days || [],
          start_time: s.start_time || "08:00",
          end_time: s.end_time || "18:00",
          interval_minutes: Number(s.interval_minutes) || 15,
          anno_da: s.anno_da ?? null,
          anno_a: s.anno_a ?? null,
        });
        if (s.anno_da != null) setSyncDa(String(s.anno_da));
        if (s.anno_a != null) setSyncA(String(s.anno_a));
        setScheduleMeta({
          next_run_at: s.next_run_at,
          last_run_at: s.last_run_at,
          last_run_ok: s.last_run_ok,
          recent_runs: s.recent_runs || [],
        });
      } catch (e) { /* ignore */ }
      try {
        const r = await getAppSettings();
        setAnnoApp(r.data?.anno_corrente || null);
        setAnnoIseeGestito(r.data?.anno_isee_gestito || r.data?.anno_corrente || null);
        setAnnoLibri(r.data?.anno_prenotazione_libri || r.data?.anno_corrente || null);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true); setResult(null);
    try { const res = await saveConfig(config); setResult({ ok: true, msg: res.data.message }); }
    catch (e) { setResult({ ok: false, msg: e.response?.data?.detail || "Errore" }); }
    finally { setSaving(false); }
  };
  const handleTest = async () => {
    setTesting(true); setResult(null);
    try { const res = await testConfig(); setResult({ ok: res.data.status, msg: res.data.message }); }
    catch (e) { setResult({ ok: false, msg: e.response?.data?.detail || "Errore" }); }
    finally { setTesting(false); }
  };

  const toggleDay = (d) => {
    setSchedule(s => ({ ...s, days: s.days.includes(d) ? s.days.filter(x => x !== d) : [...s.days, d] }));
  };
  const saveSched = async () => {
    setSchedSaving(true); setSchedMsg(null);
    try {
      const r = await saveSchedule(schedule);
      setSchedMsg({ ok: true, text: r.data.message || "Salvato" });
      setScheduleMeta(m => ({ ...m, next_run_at: r.data.next_run_at }));
    } catch (e) {
      setSchedMsg({ ok: false, text: e.response?.data?.detail || "Errore" });
    } finally { setSchedSaving(false); }
  };
  const persistAnniSync = async (daRaw, aRaw) => {
    const da = parseInt(daRaw);
    const a = parseInt(aRaw);
    if (!Number.isFinite(da) || !Number.isFinite(a)) return;
    const next = { ...schedule, anno_da: Math.min(da, a), anno_a: Math.max(da, a) };
    setSchedule(next);
    try { await saveSchedule(next); } catch { /* ignore */ }
  };
  const runNow = async () => {
    setSchedMsg(null);
    try {
      const da = parseInt(syncDa);
      const a = parseInt(syncA);
      const params = (Number.isFinite(da) && Number.isFinite(a))
        ? { anno_da: Math.min(da, a), anno_a: Math.max(da, a) }
        : {};
      const r = await runScheduleNow(params);
      setSchedMsg({ ok: true, text: `⏳ ${r.data.message || "Sync avviata"} — in corso…` });
      // Polling fino a fine sync: mostra il messaggio di completamento con l'esito.
      const startedAt = Date.now();
      const poll = async () => {
        if (Date.now() - startedAt > 20 * 60 * 1000) {
          setSchedMsg({ ok: false, text: "Timeout attesa sync: controlla il tab Segremat Sync Log" });
          return;
        }
        try {
          const s = (await getSchedule()).data || {};
          if (s.sync_running) {
            setSchedMsg({ ok: true, text: `⏳ Sincronizzazione in corso… ${s.sync_current ? `(${s.sync_current})` : ""}` });
            setTimeout(poll, 4000);
            return;
          }
          setScheduleMeta({
            next_run_at: s.next_run_at,
            last_run_at: s.last_run_at,
            last_run_ok: s.last_run_ok,
            recent_runs: s.recent_runs || [],
          });
          const last = (s.recent_runs || [])[0];
          const okRun = s.last_run_ok !== false;
          setSchedMsg({
            ok: okRun,
            text: `${okRun ? "✔ Sincronizzazione completata" : "⚠ Sincronizzazione completata con errori"}${last ? ` — ${last.message}` : ""}`,
          });
          loadSyncByAnno();
        } catch {
          setTimeout(poll, 4000);
        }
      };
      setTimeout(poll, 4000);
    } catch (e) {
      setSchedMsg({ ok: false, text: e.response?.data?.detail || "Errore" });
    }
  };

  const saveAnnoApp = async () => {
    if (!annoApp || annoApp < 2000 || annoApp > 2100) {
      setAnnoAppMsg({ ok: false, text: "Anno non valido" });
      return;
    }
    setAnnoAppSaving(true); setAnnoAppMsg(null);
    try {
      await saveAppSettings({ anno_corrente: Number(annoApp) });
      await refreshAnnoCorrente();
      setAnnoAppMsg({ ok: true, text: `Anno scolastico corrente impostato a ${annoApp}/${annoApp + 1}` });
    } catch (e) {
      setAnnoAppMsg({ ok: false, text: e.response?.data?.detail || "Errore" });
    } finally { setAnnoAppSaving(false); }
  };

  const saveAnnoIseeGestito = async () => {
    if (!annoIseeGestito || annoIseeGestito < 2000 || annoIseeGestito > 2100) {
      setAnnoIseeMsg({ ok: false, text: "Anno ISEE non valido" });
      return;
    }
    setAnnoIseeSaving(true); setAnnoIseeMsg(null);
    try {
      await saveAppSettings({ anno_isee_gestito: Number(annoIseeGestito) });
      setAnnoIseeMsg({ ok: true, text: `Anno ISEE gestito impostato a ${annoIseeGestito}` });
    } catch (e) {
      setAnnoIseeMsg({ ok: false, text: e.response?.data?.detail || "Errore" });
    } finally { setAnnoIseeSaving(false); }
  };

  const saveAnnoLibri = async () => {
    if (!annoLibri || annoLibri < 2000 || annoLibri > 2100) {
      setAnnoLibriMsg({ ok: false, text: "Anno non valido" });
      return;
    }
    setAnnoLibriSaving(true); setAnnoLibriMsg(null);
    try {
      await saveAppSettings({ anno_prenotazione_libri: Number(annoLibri) });
      setAnnoLibriMsg({ ok: true, text: `Anno prenotazione libri impostato a ${annoLibri}/${annoLibri + 1}` });
    } catch (e) {
      setAnnoLibriMsg({ ok: false, text: e.response?.data?.detail || "Errore" });
    } finally { setAnnoLibriSaving(false); }
  };

  const exportCfg = async () => {
    setCfgExporting(true); setCfgMsg(null);
    try {
      const res = await api.get("/cassa-config/export", { responseType: "blob" });
      let fname = `cassa_config_${new Date().toISOString().slice(0,10)}.json`;
      const cd = res.headers["content-disposition"] || res.headers["Content-Disposition"];
      if (cd) {
        const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
        if (m) fname = decodeURIComponent(m[1]);
      }
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setCfgMsg({ ok: true, text: `Configurazione esportata: ${fname}` });
    } catch (e) {
      setCfgMsg({ ok: false, text: e.response?.data?.detail || "Errore esportazione" });
    } finally { setCfgExporting(false); }
  };

  const importCfg = async () => {
    if (!cfgImportFile) { setCfgMsg({ ok: false, text: "Seleziona un file JSON" }); return; }
    setCfgImporting(true); setCfgMsg(null); setCfgImportResult(null);
    const fd = new FormData();
    fd.append("file", cfgImportFile);
    fd.append("mode", cfgImportMode);
    try {
      const res = await api.post("/cassa-config/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCfgImportResult(res.data.result);
      setCfgMsg({ ok: true, text: "Configurazione importata" });
    } catch (e) {
      setCfgMsg({ ok: false, text: e.response?.data?.detail || "Errore importazione" });
    } finally { setCfgImporting(false); }
  };

  if (loading) return <div className="sw-loading">Caricamento...</div>;

  const F = ({ label, field, type = "text", placeholder = "" }) => (
    <div className="sw-filter-row" style={{ marginBottom: 8 }}>
      <span className="sw-filter-label" style={{ width: 140 }}>{label}:</span>
      <input type={type} placeholder={placeholder} value={config[field]} onChange={e => setConfig({ ...config, [field]: e.target.value })} className="sw-filter-input" style={{ width: 360 }} />
    </div>
  );

  const TABS = [
    { k: "anno", l: "Setup Anni Riferimento" },
    { k: "segremat", l: "Segremat Sync Setup" },
    { k: "sync", l: "Segremat Sync Log" },
    { k: "backup", l: "Backup" },
    { k: "test_portale", l: "🧪 Test Portale Alunni" },
  ];

  return (
    <div data-testid="impostazioni-page">
      <div className="sw-page-title"><h1>Impostazioni</h1><div className="sw-breadcrumb">Home &gt; Impostazioni</div></div>

      <div style={{ margin: "0 20px 12px", display: "flex", gap: 0, borderBottom: "2px solid #37474f", flexWrap: "wrap" }}>
        {TABS.map(t => {
          const active = activeTab === t.k;
          const isDanger = t.k === "pn-maint";
          return (
            <button key={t.k} data-testid={`imp-tab-${t.k}`}
              onClick={() => setActiveTab(t.k)}
              style={{
                padding: "10px 18px",
                background: active ? (isDanger ? "#7f0000" : "#37474f") : "transparent",
                color: active ? "white" : (isDanger ? "#7f0000" : "#37474f"),
                border: "none",
                borderBottom: active ? "none" : "1px solid transparent",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.3,
                cursor: "pointer",
                textTransform: "uppercase",
              }}>{t.l}</button>
          );
        })}
      </div>

      {activeTab === "anno" && (
      <>      <div className="sw-card">
        <div className="sw-card-header">Anno Scolastico Corrente</div>
        <div className="sw-card-body">
          <div className="sw-filter-row" style={{ marginBottom: 8 }}>
            <span className="sw-filter-label" style={{ width: 220 }}>Anno scolastico in corso:</span>
            <input
              data-testid="anno-corrente-input"
              type="number"
              min={2000}
              max={2100}
              value={annoApp ?? ""}
              onChange={e => setAnnoApp(Number(e.target.value) || "")}
              className="sw-filter-input"
              style={{ width: 120 }}
            />
            <span style={{ fontSize: 12, color: "#444", fontWeight: 600 }}>
              {annoApp ? `⇒ anno scolastico ${annoApp}/${Number(annoApp) + 1}` : ""}
            </span>
          </div>
          <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center" }}>
            <button data-testid="save-anno-corrente" className="sw-filter-btn sw-filter-btn-primary" onClick={saveAnnoApp} disabled={annoAppSaving}>
              {annoAppSaving ? "Salvataggio..." : "Salva"}
            </button>
            {annoAppMsg && <span style={{ fontSize: 12, fontWeight: 600, color: annoAppMsg.ok ? "#006600" : "#cc0000" }}>{annoAppMsg.text}</span>}
          </div>
          <div style={{ marginTop: 10, padding: 10, background: "#f4f9ff", border: "1px solid #cce", fontSize: 11, color: "#234" }}>
            Inserisci l'anno di partenza dell'anno scolastico. Esempio: se l'anno scolastico va da <b>settembre 2025 a giugno 2026</b>, inserisci <b>2025</b>. Questo valore viene usato come default per i filtri anno in tutta l'applicazione (Alunni, Genitori, Classi, Movimenti, Elaborazioni, Importazioni).
          </div>
        </div>
      </div>

      <div className="sw-card" style={{ marginTop: 12 }}>
        <div className="sw-card-header">Anno ISEE Gestito</div>
        <div className="sw-card-body">
          <div className="sw-filter-row" style={{ marginBottom: 8 }}>
            <span className="sw-filter-label" style={{ width: 220 }}>Anno ISEE attualmente gestito:</span>
            <input
              data-testid="anno-isee-gestito-input"
              type="number"
              min={2000}
              max={2100}
              value={annoIseeGestito ?? ""}
              onChange={e => setAnnoIseeGestito(Number(e.target.value) || "")}
              className="sw-filter-input"
              style={{ width: 120 }}
            />
            <span style={{ fontSize: 12, color: "#444", fontWeight: 600 }}>
              {annoIseeGestito ? `(anno ISEE = ${annoIseeGestito})` : ""}
            </span>
          </div>
          <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center" }}>
            <button data-testid="save-anno-isee-gestito" className="sw-filter-btn sw-filter-btn-primary" onClick={saveAnnoIseeGestito} disabled={annoIseeSaving}>
              {annoIseeSaving ? "Salvataggio..." : "Salva"}
            </button>
            {annoIseeMsg && <span style={{ fontSize: 12, fontWeight: 600, color: annoIseeMsg.ok ? "#006600" : "#cc0000" }}>{annoIseeMsg.text}</span>}
          </div>
          <div style={{ marginTop: 10, padding: 10, background: "#e0f7fa", border: "1px solid #00838f", fontSize: 11, color: "#006064" }}>
            Indica l'anno ISEE che viene mostrato di default nelle pagine Mensa Mese. Indipendente dall'anno scolastico: ad es. nell'AS 2025/26 puoi scegliere di mostrare gli ISEE dell'anno <b>2025</b> o quelli dell'anno <b>2026</b>. La pagina <b>Mensa Mese</b> mostra comunque un selettore "Anno ISEE" per cambiare temporaneamente la vista.
          </div>
        </div>
      </div>

      <div className="sw-card" style={{ marginTop: 12 }}>
        <div className="sw-card-header">Anno Prenotazione Libri di Testo</div>
        <div className="sw-card-body">
          <div className="sw-filter-row" style={{ marginBottom: 8 }}>
            <span className="sw-filter-label" style={{ width: 220 }}>Anno scolastico prenotazioni libri:</span>
            <input
              data-testid="anno-prenotazione-libri-input"
              type="number"
              min={2000}
              max={2100}
              value={annoLibri ?? ""}
              onChange={e => setAnnoLibri(Number(e.target.value) || "")}
              className="sw-filter-input"
              style={{ width: 120 }}
            />
            <span style={{ fontSize: 12, color: "#444", fontWeight: 600 }}>
              {annoLibri ? `⇒ A.S. ${annoLibri}/${Number(annoLibri) + 1}` : ""}
            </span>
          </div>
          <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center" }}>
            <button data-testid="save-anno-prenotazione-libri" className="sw-filter-btn sw-filter-btn-primary" onClick={saveAnnoLibri} disabled={annoLibriSaving}>
              {annoLibriSaving ? "Salvataggio..." : "Salva"}
            </button>
            {annoLibriMsg && <span style={{ fontSize: 12, fontWeight: 600, color: annoLibriMsg.ok ? "#006600" : "#cc0000" }}>{annoLibriMsg.text}</span>}
          </div>
          <div style={{ marginTop: 10, padding: 10, background: "#fff8e1", border: "1px solid #ffb300", fontSize: 11, color: "#5d4037" }}>
            Anno scolastico per cui la segreteria sta raccogliendo gli <b>acconti per i libri di testo</b> e le adesioni al <b>servizio copertine</b>. Determina la classe in cui ogni alunno appare nella pagina "📚 Prenotazioni Libri" della gestione segremat.
          </div>
        </div>
      </div>
      </>
      )}

      {activeTab === "segremat" && (
      <>
      <div className="sw-card">
        <div className="sw-card-header">Connessione API Segremat Web</div>
        <div className="sw-card-body">
          <F label="Codice Piattaforma" field="platform_code" placeholder="Es. SGR_0379" />
          <F label="URL Base" field="base_url" placeholder="https://scuolaonline.soluzione-web.it" />
          <F label="Username API" field="username" placeholder="Username" />
          <F label="Password API" field="password" type="password" placeholder="Password" />
          <F label="Chiave Segreta" field="secret_key" type="password" placeholder="Secret Key" />
          <div style={{ marginTop: 16, display: "flex", gap: 8, alignItems: "center" }}>
            <button data-testid="save-config-btn" className="sw-filter-btn sw-filter-btn-primary" disabled={saving} onClick={handleSave}>{saving ? "Salvataggio..." : "Salva Configurazione"}</button>
            <button data-testid="test-config-btn" className="sw-filter-btn" disabled={testing} onClick={handleTest}>{testing ? "Test..." : "Testa Connessione"}</button>
            {result && <span style={{ fontSize: 12, fontWeight: 600, color: result.ok ? "#006600" : "#cc0000" }}>{result.msg}</span>}
          </div>
        </div>
      </div>

      {/* Scheduled Sync */}
      <div className="sw-card">
        <div className="sw-card-header" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span>Sincronizzazione Schedulata</span>
          <span
            data-testid="schedule-status-badge"
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.5,
              padding: "3px 10px",
              borderRadius: 12,
              background: schedule.enabled ? "#2e7d32" : "#9e9e9e",
              color: "white",
              textTransform: "uppercase",
            }}
          >
            {schedule.enabled ? "● ATTIVA" : "○ DISATTIVA"}
          </span>
        </div>
        <div className="sw-card-body">
          <div className="sw-filter-row" style={{ marginBottom: 10 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }} data-testid="schedule-enabled">
              <input type="checkbox" checked={schedule.enabled} onChange={e => setSchedule({ ...schedule, enabled: e.target.checked })} />
              Attiva sincronizzazione automatica
            </label>
            {!schedule.enabled && (
              <span style={{ fontSize: 11, color: "#888", fontStyle: "italic", marginLeft: 8 }}>
                (nessuna esecuzione automatica verrà eseguita)
              </span>
            )}
          </div>

          <div className="sw-filter-row" style={{ marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
            <span className="sw-filter-label" style={{ width: 140 }}>Giorni attivi:</span>
            {DAYS.map(d => (
              <label key={d.key} data-testid={`schedule-day-${d.key}`} style={{
                display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer",
                padding: "6px 12px", border: `1px solid ${schedule.days.includes(d.key) ? "#0066cc" : "#ccc"}`,
                borderRadius: 2, background: schedule.days.includes(d.key) ? "#e8f0fe" : "white", fontWeight: 600,
              }}>
                <input type="checkbox" checked={schedule.days.includes(d.key)} onChange={() => toggleDay(d.key)} />
                {d.label}
              </label>
            ))}
          </div>

          <div className="sw-filter-row" style={{ marginBottom: 10 }}>
            <span className="sw-filter-label" style={{ width: 140 }}>Finestra oraria:</span>
            <span style={{ fontSize: 12, color: "#666" }}>dalle</span>
            <input data-testid="schedule-start" type="time" value={schedule.start_time} onChange={e => setSchedule({ ...schedule, start_time: e.target.value })} className="sw-filter-input" style={{ width: 100 }} />
            <span style={{ fontSize: 12, color: "#666" }}>alle</span>
            <input data-testid="schedule-end" type="time" value={schedule.end_time} onChange={e => setSchedule({ ...schedule, end_time: e.target.value })} className="sw-filter-input" style={{ width: 100 }} />
          </div>

          <div className="sw-filter-row" style={{ marginBottom: 10 }}>
            <span className="sw-filter-label" style={{ width: 140 }}>Intervallo:</span>
            <input data-testid="schedule-interval" type="number" min={1} max={1440} value={schedule.interval_minutes} onChange={e => setSchedule({ ...schedule, interval_minutes: Number(e.target.value) || 1 })} className="sw-filter-input" style={{ width: 100 }} />
            <span style={{ fontSize: 12, color: "#666" }}>minuti</span>
          </div>

          <div className="sw-filter-row" style={{ marginBottom: 10 }}>
            <span className="sw-filter-label" style={{ width: 140 }}>Sincronizza dall&apos;anno:</span>
            <input type="number" min="2000" max="2100" step="1" data-testid="sync-da-input"
              value={syncDa} onChange={e => setSyncDa(e.target.value)}
              onBlur={() => persistAnniSync(syncDa, syncA)}
              style={{ width: 90, padding: "4px 8px", fontFamily: "monospace", textAlign: "center", fontSize: 14, fontWeight: 700, border: "2px solid #2e7d32" }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: "#234" }}>all&apos;anno:</span>
            <input type="number" min="2000" max="2100" step="1" data-testid="sync-a-input"
              value={syncA} onChange={e => setSyncA(e.target.value)}
              onBlur={() => persistAnniSync(syncDa, syncA)}
              style={{ width: 90, padding: "4px 8px", fontFamily: "monospace", textAlign: "center", fontSize: 14, fontWeight: 700, border: "2px solid #2e7d32" }} />
            <span style={{ fontSize: 11, color: "#666", fontStyle: "italic" }}>
              {(() => { const a = parseInt(syncDa), b = parseInt(syncA); if (!Number.isFinite(a) || !Number.isFinite(b)) return ""; return `(${Math.abs(b - a) + 1} anni)`; })()}
            </span>
            <span style={{ fontSize: 11, color: "#666" }}>— vale per la sync programmata e per quella manuale; gli anni fuori intervallo vengono eliminati</span>
          </div>

          <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <button data-testid="save-schedule-btn" className="sw-filter-btn sw-filter-btn-primary" disabled={schedSaving} onClick={saveSched}>{schedSaving ? "Salvataggio..." : "Salva pianificazione"}</button>
            {schedMsg && <span style={{ fontSize: 12, fontWeight: 600, color: schedMsg.ok ? "#006600" : "#cc0000" }}>{schedMsg.text}</span>}
          </div>

          <div style={{ marginTop: 16, padding: 10, background: "#f4f9ff", border: "1px solid #cce", fontSize: 12, color: "#234", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            <div><b>Prossima esecuzione:</b> {fmtDateTime(scheduleMeta.next_run_at)}</div>
            <div><b>Ultima esecuzione:</b> {fmtDateTime(scheduleMeta.last_run_at)} {scheduleMeta.last_run_ok === false && <span style={{ color: "#c00" }}>(con errori)</span>}</div>
          </div>
        </div>
      </div>
      {/* Sync manuale (spostata qui dal tab Log su richiesta utente) */}
      <div className="sw-card" data-testid="manual-sync-card">
        <div className="sw-card-header">Sincronizzazione manuale</div>
        <div className="sw-card-body">
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <button data-testid="run-now-btn" className="sw-filter-btn sw-filter-btn-primary" style={{ background: "#2e7d32" }} onClick={runNow}>▶ Avvia sync adesso</button>
            {schedMsg && <span style={{ fontSize: 12, fontWeight: 600, color: schedMsg.ok ? "#006600" : "#cc0000" }}>{schedMsg.text}</span>}
          </div>
          <div style={{ fontSize: 11, color: "#666", marginTop: 8 }}>
            Sincronizza subito l&apos;intervallo anni impostato nella sezione qui sopra. L&apos;esito è consultabile nel tab <b>Segremat Sync Log</b>.
          </div>
        </div>
      </div>
      </>
      )}

      {activeTab === "sync" && (
      <div className="sw-card" data-testid="sync-history-card">
        <div className="sw-card-header">Segremat Sync Log — Ultime esecuzioni</div>
        <div className="sw-card-body">
          <div style={{ padding: 10, background: "#f4f9ff", border: "1px solid #cce", fontSize: 12, color: "#234", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 14 }}>
            <div><b>Prossima esecuzione:</b> {fmtDateTime(scheduleMeta.next_run_at)}</div>
            <div><b>Ultima esecuzione:</b> {fmtDateTime(scheduleMeta.last_run_at)} {scheduleMeta.last_run_ok === false && <span style={{ color: "#c00" }}>(con errori)</span>}</div>
          </div>

          <div style={{ marginBottom: 18, border: "1px solid #ccc" }}>
            <div style={{ background: "#37474f", color: "white", padding: "6px 10px", fontSize: 12, fontWeight: 700, letterSpacing: 0.5 }}>📊 Stato sincronizzazione per Anno</div>
            {syncByAnno.length === 0 ? (
              <div style={{ padding: 14, fontSize: 12, color: "#666" }}>Nessun dato ancora disponibile. Avvia una sync per popolare le statistiche.</div>
            ) : (
              <table className="sw-table" style={{ fontSize: 11 }}>
                <thead>
                  <tr>
                    <th style={{ width: 70, textAlign: "center" }}>Anno</th>
                    <th style={{ width: 100, textAlign: "right" }}>Alunni</th>
                    <th style={{ width: 100, textAlign: "right" }}>Genitori</th>
                    <th style={{ width: 80, textAlign: "right" }}>Classi</th>
                    <th style={{ width: 110, textAlign: "right" }}>Movimenti</th>
                    <th style={{ width: 160 }}>Ultima Anagrafica</th>
                    <th>Ultima Movimenti</th>
                  </tr>
                </thead>
                <tbody>
                  {syncByAnno.map(r => {
                    const neverSynced = !r.last_anag_at && !r.last_mov_at;
                    return (
                      <tr key={r.anno} data-testid={`sync-anno-${r.anno}`} style={{ background: neverSynced ? "#fff3e0" : "transparent" }}>
                        <td style={{ fontWeight: 800, textAlign: "center", fontFamily: "monospace" }}>{r.anno}</td>
                        <td style={{ textAlign: "right", fontFamily: "monospace", color: r.db_alunni ? "#1b5e20" : "#c62828", fontWeight: 700 }}>{(r.db_alunni || 0).toLocaleString("it-IT")}</td>
                        <td style={{ textAlign: "right", fontFamily: "monospace" }}>{(r.db_genitori || 0).toLocaleString("it-IT")}</td>
                        <td style={{ textAlign: "right", fontFamily: "monospace" }}>{r.db_classi || 0}</td>
                        <td style={{ textAlign: "right", fontFamily: "monospace", color: r.db_movimenti ? "#1b5e20" : "#c62828", fontWeight: 700 }}>{(r.db_movimenti || 0).toLocaleString("it-IT")}</td>
                        <td style={{ fontFamily: "monospace", color: r.last_anag_at ? "#333" : "#c62828" }}>{r.last_anag_at ? fmtDateTime(r.last_anag_at) : "— mai —"}</td>
                        <td style={{ fontFamily: "monospace", color: r.last_mov_at ? "#333" : "#c62828" }}>{r.last_mov_at ? fmtDateTime(r.last_mov_at) : "— mai —"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {(!scheduleMeta.recent_runs || scheduleMeta.recent_runs.length === 0) ? (
            <div style={{ padding: 30, textAlign: "center", color: "#888", fontSize: 12, border: "1px solid #ddd" }}>
              Nessuna esecuzione registrata. Avvia la prima sync dal tab <b>Segremat Sync Setup</b>.
            </div>
          ) : (
            <table className="sw-table" style={{ fontSize: 11 }}>
              <thead><tr><th style={{ width: 140 }}>Quando</th><th style={{ width: 80 }}>Stato</th><th>Messaggio</th></tr></thead>
              <tbody>
                {scheduleMeta.recent_runs.map((r, i) => (
                  <tr key={i}>
                    <td>{fmtDateTime(r.timestamp)}</td>
                    <td><span style={{ background: r.status === "ok" ? "#2e7d32" : "#c62828", color: "white", padding: "1px 8px", borderRadius: 2, fontSize: 10 }}>{r.status}</span></td>
                    <td>{r.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      )}

      {activeTab === "cassa" && (
      <div className="sw-card" data-testid="cfg-cassa-card">
        <div className="sw-card-header">Configurazione gestione Cassa — Esporta / Importa</div>
        <div className="sw-card-body">
          <div style={{ fontSize: 12, color: "#555", marginBottom: 10, lineHeight: 1.6 }}>
            Esporta lo schema PDC, le tabelle (causali/modalità/settori) e i gruppi come file JSON.
            Puoi poi importarlo su un'altra installazione (es. <b>produzione</b>) per replicare
            la configurazione senza rifare manualmente i gruppi.
            <br /><b>Attenzione:</b> l'import in modalità <code>replace</code> svuota le collezioni
            di configurazione prima di reimportarle. I <i>dati</i> (alunni, movimenti, elaborazioni) NON vengono toccati.
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 10 }}>
            <button data-testid="btn-cfg-export" onClick={exportCfg} disabled={cfgExporting}
              className="sw-filter-btn sw-filter-btn-primary" style={{ background: "#2e7d32" }}>
              {cfgExporting ? "Esportazione..." : "📤 Esporta configurazione"}
            </button>
            <div style={{ width: 1, height: 30, background: "#ccc" }} />
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 3 }}>File JSON da importare</label>
              <input data-testid="in-cfg-import-file" type="file" accept=".json,application/json"
                onChange={e => setCfgImportFile(e.target.files?.[0] || null)}
                style={{ fontSize: 12 }} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 3 }}>Modalità</label>
              <select data-testid="sel-cfg-import-mode" value={cfgImportMode} onChange={e => setCfgImportMode(e.target.value)}
                className="sw-filter-input" style={{ width: 200 }}>
                <option value="replace">Replace (svuota poi importa)</option>
                <option value="merge">Merge (solo insert)</option>
              </select>
            </div>
            <button data-testid="btn-cfg-import" onClick={importCfg} disabled={cfgImporting || !cfgImportFile}
              className="sw-filter-btn sw-filter-btn-primary" style={{ background: "#5e35b1" }}>
              {cfgImporting ? "Importazione..." : "📥 Importa"}
            </button>
          </div>
          {cfgMsg && (
            <div style={{ padding: 8, fontSize: 12, background: cfgMsg.ok ? "#eeffee" : "#ffeeee", border: `1px solid ${cfgMsg.ok ? "#aea" : "#eaa"}`, color: cfgMsg.ok ? "#060" : "#c00", marginBottom: 8 }}>
              {cfgMsg.text}
            </div>
          )}
          {cfgImportResult && (
            <div data-testid="cfg-import-result" style={{ background: "#f4f9ff", border: "1px solid #cce", padding: 10, fontSize: 12 }}>
              <b>Risultato import:</b>
              <table style={{ marginTop: 4, fontSize: 12 }}>
                <tbody>
                  {Object.entries(cfgImportResult).map(([coll, info]) => (
                    <tr key={coll}>
                      <td style={{ fontFamily: "Consolas, monospace", paddingRight: 12 }}>{coll}</td>
                      <td>
                        {info.skipped
                          ? <span style={{ color: "#888" }}>saltato ({info.reason})</span>
                          : <>
                              <b>{info.imported}</b> record importati
                              {info.cleared_first && <span style={{ color: "#888" }}> · collezione svuotata prima</span>}
                            </>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      )}

      {activeTab === "backup" && (
      <div className="sw-card" data-testid="backup-card">
        <div className="sw-card-header">Backup automatico del database</div>
        <div className="sw-card-body">
          <div style={{ fontSize: 12, color: "#555", marginBottom: 12, lineHeight: 1.6 }}>
            Salva un archivio ZIP completo con tutte le collezioni MongoDB e gli allegati GridFS.
            I backup sono salvati nel database (persistono ad ogni redeploy) e sono scaricabili da qui.
            <br />La schedulazione esegue un backup automatico all'ora indicata; viene conservato
            solo il numero di backup indicato (rotazione), gli altri vengono eliminati.
          </div>

          {backupCfg && (
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 14 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600 }}>
                <input data-testid="backup-enabled" type="checkbox" checked={!!backupCfg.enabled}
                  onChange={e => setBackupCfg({ ...backupCfg, enabled: e.target.checked })} />
                Backup giornaliero attivo
              </label>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 3 }}>Orario</label>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <input data-testid="backup-hour" type="number" min={0} max={23} value={backupCfg.hour}
                    onChange={e => setBackupCfg({ ...backupCfg, hour: Math.max(0, Math.min(23, Number(e.target.value) || 0)) })}
                    style={{ width: 60, padding: 5, fontSize: 13, border: "1px solid #bbb", fontFamily: "Consolas, monospace", textAlign: "center" }} />
                  <span>:</span>
                  <input data-testid="backup-minute" type="number" min={0} max={59} value={backupCfg.minute}
                    onChange={e => setBackupCfg({ ...backupCfg, minute: Math.max(0, Math.min(59, Number(e.target.value) || 0)) })}
                    style={{ width: 60, padding: 5, fontSize: 13, border: "1px solid #bbb", fontFamily: "Consolas, monospace", textAlign: "center" }} />
                  <span style={{ fontSize: 11, color: "#888", marginLeft: 4 }}>(Europe/Rome)</span>
                </div>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 3 }}>Conserva ultimi N backup</label>
                <input data-testid="backup-retention" type="number" min={1} max={365} value={backupCfg.retention_count}
                  onChange={e => setBackupCfg({ ...backupCfg, retention_count: Math.max(1, Math.min(365, Number(e.target.value) || 1)) })}
                  style={{ width: 80, padding: 5, fontSize: 13, border: "1px solid #bbb", fontFamily: "Consolas, monospace", textAlign: "center" }} />
              </div>
              <div style={{ borderTop: "1px dashed #cbd5e1", paddingTop: 10, marginTop: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#334155", marginBottom: 6 }}>📧 Sicurezza extra — copia off-site via email</div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 3 }}>Email destinatario</label>
                <input data-testid="backup-notify-email" type="email" value={backupCfg.notify_email || ""}
                  onChange={e => setBackupCfg({ ...backupCfg, notify_email: e.target.value })}
                  placeholder="es. dirigente@canossadiaz.it"
                  style={{ width: 300, maxWidth: "100%", padding: 5, fontSize: 13, border: "1px solid #bbb" }} />
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginTop: 8, cursor: "pointer" }} data-testid="backup-email-attach">
                  <input type="checkbox" checked={!!backupCfg.email_backup}
                    onChange={e => setBackupCfg({ ...backupCfg, email_backup: e.target.checked })} />
                  📎 Invia il file di backup in allegato (copia off-site) <span style={{ color: "#888", fontSize: 11 }}>(solo se &lt; ~38 MB)</span>
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginTop: 6, cursor: "pointer" }} data-testid="backup-email-notify">
                  <input type="checkbox" checked={!!backupCfg.email_notify}
                    onChange={e => setBackupCfg({ ...backupCfg, email_notify: e.target.checked })} />
                  🔔 Inviami una notifica con l'esito di ogni backup (successo/errore)
                </label>
              </div>
              <button data-testid="btn-save-backup-cfg" onClick={saveBackupCfg} disabled={backupBusy}
                className="sw-filter-btn sw-filter-btn-primary" style={{ background: "#0066cc" }}>
                Salva pianificazione
              </button>
              <button data-testid="btn-run-backup-now" onClick={runBackupNow} disabled={backupBusy}
                className="sw-filter-btn sw-filter-btn-primary" style={{ background: "#2e7d32" }}>
                {backupBusy ? "In corso..." : "💾 Esegui backup adesso"}
              </button>
            </div>
          )}

          {backupMsg && (
            <div style={{ padding: 8, fontSize: 12, background: backupMsg.ok ? "#eeffee" : "#ffeeee", border: `1px solid ${backupMsg.ok ? "#aea" : "#eaa"}`, color: backupMsg.ok ? "#060" : "#c00", marginBottom: 10 }}>
              {backupMsg.text}
            </div>
          )}

          <div style={{ padding: 8, background: "#f4f9ff", border: "1px solid #cce", fontSize: 12, marginBottom: 12 }}>
            <b>Prossima esecuzione:</b> {backupNextRun ? fmtDateTime(backupNextRun) : <span style={{ color: "#888" }}>— (disattivo)</span>}
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Backup disponibili ({backups.length})</div>
          {backups.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: "#888", fontSize: 12, border: "1px solid #ddd" }}>
              Nessun backup ancora eseguito. Clicca "Esegui backup adesso" per crearne uno.
            </div>
          ) : (
            <div className="sw-table-container" style={{ maxHeight: 360, overflowY: "auto" }}>
              <table className="sw-table" style={{ fontSize: 12 }}>
                <thead>
                  <tr>
                    <th>QUANDO</th>
                    <th>FILE</th>
                    <th>DIMENSIONE</th>
                    <th>SORGENTE</th>
                    <th>COLLEZIONI</th>
                    <th className="center-col" style={{ width: 140 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {backups.map(b => (
                    <tr key={b.chiave} data-testid={`backup-${b.chiave}`}>
                      <td>{fmtDateTime(b.created_at)}</td>
                      <td style={{ fontFamily: "Consolas, monospace", fontSize: 11 }}>{b.filename}</td>
                      <td style={{ fontFamily: "Consolas, monospace" }}>{((b.size || 0) / 1024 / 1024).toFixed(2)} MB</td>
                      <td>
                        <span style={{ background: b.source === "scheduled" ? "#e3f2fd" : "#e8f5e9", color: b.source === "scheduled" ? "#0277bd" : "#2e7d32", padding: "2px 6px", fontSize: 10, fontWeight: 700, borderRadius: 2 }}>
                          {b.source === "scheduled" ? "AUTO" : "MANUALE"}
                        </span>
                      </td>
                      <td style={{ fontSize: 11 }}>
                        {Object.keys(b.collections || {}).length} coll · {Object.values(b.gridfs || {}).reduce((a, n) => a + n, 0)} file
                      </td>
                      <td className="center-col" style={{ whiteSpace: "nowrap" }}>
                        <button onClick={() => downloadBackup(b.chiave)} title="Scarica ZIP"
                          style={{ background: "#0066cc", color: "white", border: "none", padding: "3px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", marginRight: 4 }}>⬇</button>
                        <button onClick={() => restorePrimaNota(b.chiave)} title="Ripristina SOLO Prima Nota (fatture + costi contabili) da questo backup"
                          data-testid={`btn-restore-pn-${b.chiave}`}
                          disabled={backupBusy}
                          style={{ background: "#6a1b9a", color: "white", border: "none", padding: "3px 10px", fontSize: 11, fontWeight: 700, cursor: backupBusy ? "wait" : "pointer", marginRight: 4 }}>↺ PN</button>
                        <button onClick={() => restoreAnagraficaFornitori(b.chiave)} title="Ripristina SOLO l'anagrafica fornitori (pn_anagrafica) da questo backup"
                          data-testid={`btn-restore-anag-${b.chiave}`}
                          disabled={backupBusy}
                          style={{ background: "#1565c0", color: "white", border: "none", padding: "3px 10px", fontSize: 11, fontWeight: 700, cursor: backupBusy ? "wait" : "pointer", marginRight: 4 }}>↺ Fornitori</button>
                        <button onClick={() => openSelectiveRestore(b.chiave)} title="Ripristino SELETTIVO: scegli quali tabelle ripristinare"
                          data-testid={`btn-restore-selective-${b.chiave}`}
                          disabled={backupBusy || selectiveBusy}
                          style={{ background: "#00838f", color: "white", border: "none", padding: "3px 10px", fontSize: 11, fontWeight: 700, cursor: (backupBusy || selectiveBusy) ? "wait" : "pointer", marginRight: 4 }}>☑ Selettivo</button>
                        <button onClick={() => restoreCompleto(b.chiave)} title="🚨 RIPRISTINO COMPLETO: sovrascrive l'INTERO database con questo backup"
                          data-testid={`btn-restore-full-${b.chiave}`}
                          disabled={backupBusy}
                          style={{ background: "#c62828", color: "white", border: "none", padding: "3px 10px", fontSize: 11, fontWeight: 800, cursor: backupBusy ? "wait" : "pointer", marginRight: 4 }}>↺ TUTTO</button>
                        <button onClick={() => deleteBackup(b.chiave)} title="Elimina"
                          data-testid={`btn-del-backup-${b.chiave}`}
                          style={{ background: "white", color: "#c62828", border: "1px solid #c62828", padding: "3px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      )}

      {/* MODAL: Restore Selettivo */}
      {selectiveOpen && (
        <div onClick={() => !backupBusy && setSelectiveOpen(null)}
             style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 900, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 30, overflowY: "auto" }}>
          <div onClick={(e) => e.stopPropagation()}
               data-testid="selective-restore-modal"
               style={{ background: "white", width: "100%", maxWidth: 720, borderRadius: 6, overflow: "hidden" }}>
            <div style={{ background: "#00838f", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800 }}>☑ Restore selettivo · backup {selectiveOpen.chiave}</div>
                <div style={{ fontSize: 11, opacity: 0.9 }}>{selectiveOpen.collections.length} collezioni nel backup — seleziona quali sostituire</div>
              </div>
              <button onClick={() => !backupBusy && setSelectiveOpen(null)} disabled={backupBusy}
                      style={{ background: "none", border: "none", color: "white", fontSize: 24, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ padding: 12 }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <button onClick={() => setSelectiveOpen((s) => ({ ...s, collections: s.collections.map((c) => ({ ...c, selected: true })) }))}
                        data-testid="sel-restore-all"
                        style={{ padding: "4px 10px", fontSize: 11, fontWeight: 700, background: "white", border: "1px solid #ccc", cursor: "pointer" }}>
                  Seleziona tutto
                </button>
                <button onClick={() => setSelectiveOpen((s) => ({ ...s, collections: s.collections.map((c) => ({ ...c, selected: false })) }))}
                        data-testid="sel-restore-none"
                        style={{ padding: "4px 10px", fontSize: 11, fontWeight: 700, background: "white", border: "1px solid #ccc", cursor: "pointer" }}>
                  Deseleziona
                </button>
                <span style={{ marginLeft: "auto", fontSize: 11, color: "#666" }}>
                  Selezionate: <b>{selectiveOpen.collections.filter((c) => c.selected).length}</b>
                </span>
              </div>
              <div style={{ maxHeight: 420, overflowY: "auto", border: "1px solid #ddd", borderRadius: 3 }}>
                <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                  <thead style={{ background: "#f5f5f5", position: "sticky", top: 0 }}>
                    <tr>
                      <th style={{ width: 30, padding: "6px 8px", textAlign: "center" }}></th>
                      <th style={{ padding: "6px 8px", textAlign: "left" }}>COLLEZIONE</th>
                      <th style={{ padding: "6px 8px", textAlign: "right", width: 100 }}>RECORD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectiveOpen.collections.map((c, i) => (
                      <tr key={c.name} style={{ background: c.selected ? "#fff3e0" : (i % 2 ? "#fafafa" : "white"), borderTop: "1px solid #eee" }}>
                        <td style={{ textAlign: "center", padding: "4px 8px" }}>
                          <input type="checkbox" checked={c.selected}
                                 data-testid={`sel-coll-${c.name}`}
                                 onChange={(e) => setSelectiveOpen((s) => ({
                                   ...s,
                                   collections: s.collections.map((x) => x.name === c.name ? { ...x, selected: e.target.checked } : x),
                                 }))} />
                        </td>
                        <td style={{ padding: "4px 8px", fontFamily: "Consolas, monospace", fontWeight: 600 }}>{c.name}</td>
                        <td style={{ padding: "4px 8px", textAlign: "right", fontFamily: "Consolas, monospace", color: "#666" }}>{c.count.toLocaleString("it-IT")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div style={{ borderTop: "1px solid #eee", padding: "10px 16px", display: "flex", justifyContent: "flex-end", gap: 8, background: "#fafafa" }}>
              <button onClick={() => setSelectiveOpen(null)} disabled={backupBusy}
                      style={{ padding: "8px 16px", background: "white", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                Annulla
              </button>
              <button onClick={executeSelectiveRestore} disabled={backupBusy || selectiveOpen.collections.filter((c) => c.selected).length === 0}
                      data-testid="sel-restore-execute"
                      style={{ padding: "8px 20px", background: "#ef6c00", color: "white", border: "none", fontSize: 13, fontWeight: 700, cursor: backupBusy ? "wait" : "pointer", borderRadius: 3 }}>
                {backupBusy ? "Ripristino…" : `↺ RIPRISTINA ${selectiveOpen.collections.filter((c) => c.selected).length} collezione/i`}
              </button>
            </div>
          </div>
        </div>
      )}
      {activeTab === "pn-maint" && (
      <div className="sw-card" data-testid="pn-maintenance-card">
        <div className="sw-card-header" style={{ background: "#7f0000", color: "white" }}>⚠ Manutenzione Prima Nota — Operazioni Distruttive</div>
        <div className="sw-card-body">
          <div style={{ fontSize: 12, color: "#555", marginBottom: 12, lineHeight: 1.6 }}>
            Strumenti per ricaricare l'anagrafica fornitori e la Prima Nota da zero.
            <br /><b style={{ color: "#c62828" }}>⚠ Operazioni IRREVERSIBILI</b>: prima di eseguire, assicurati di aver fatto un backup recente (sezione qui sopra).
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
            {/* 1) CANCELLA FORNITORI TUTTI */}
            <button data-testid="pn-maint-del-fornitori"
              onClick={async () => {
                const conf = window.prompt(
                  "⚠ CANCELLAZIONE TOTALE FORNITORI ⚠\n\n" +
                  "Verranno cancellati TUTTI i fornitori (pn_anagrafica).\n" +
                  "Le fatture (pn_fatture) NON verranno toccate — restano con il loro id_cliente.\n\n" +
                  "Per confermare, digita esattamente la parola: CANCELLA"
                );
                if (conf !== "CANCELLA") { if (conf !== null) alert("Operazione annullata."); return; }
                try {
                  const r = await api.post("/pn/anagrafica/reset-all", null, { params: { confirm: "CANCELLA" } });
                  alert(`✓ Cancellati ${r.data?.deleted_fornitori || 0} fornitori.`);
                } catch (e) {
                  alert("Errore: " + (e.response?.data?.detail || e.message));
                }
              }}
              style={{ padding: "14px 16px", background: "#7f0000", color: "white", border: "none", fontSize: 13, fontWeight: 800, cursor: "pointer", letterSpacing: 0.3 }}>
              🗑 CANCELLA FORNITORI TUTTI
            </button>

            {/* 2) CANCELLA PRIMANOTA TUTTA */}
            <button data-testid="pn-maint-del-primanota"
              onClick={async () => {
                const conf = window.prompt(
                  "⚠ CANCELLAZIONE TOTALE PRIMA NOTA ⚠\n\n" +
                  "Verranno CANCELLATE tutte le Fatture, Note di Credito, Operazioni Extra e i Costi Contabili collegati.\n\n" +
                  "Per confermare, digita esattamente la parola: CANCELLA"
                );
                if (conf !== "CANCELLA") { if (conf !== null) alert("Operazione annullata."); return; }
                if (!window.confirm("ULTIMA CONFERMA: procedere?")) return;
                try {
                  const r = await api.post("/pn/maintenance/reset-all", null, { params: { confirm: "CANCELLA" } });
                  alert(`✓ Cancellati ${r.data?.deleted_fatture || 0} documenti e ${r.data?.deleted_costi_contabili || 0} righe contabili.`);
                } catch (e) {
                  alert("Errore: " + (e.response?.data?.detail || e.message));
                }
              }}
              style={{ padding: "14px 16px", background: "#7f0000", color: "white", border: "none", fontSize: 13, fontWeight: 800, cursor: "pointer", letterSpacing: 0.3 }}>
              🗑 CANCELLA PRIMANOTA TUTTA
            </button>

            {/* 3) IMPORTA FOGLIO EXCEL FORNITORI */}
            <button data-testid="pn-maint-import-fornitori"
              onClick={async () => {
                const url = window.prompt(
                  "URL del file Excel Fornitori (Anagrafica):",
                  "https://customer-assets.emergentagent.com/job_data-extraction-tool-8/artifacts/"
                );
                if (!url || !url.trim()) return;
                const mode = window.confirm(
                  "Modalità di import:\n\n" +
                  "• OK → MERGE per P.IVA (preserva i PK esistenti, le fatture restano collegate)\n" +
                  "• Annulla → REPLACE (cancella tutto e genera nuovi UUID — le fatture diventano orfane)"
                ) ? "merge-by-piva" : "replace";
                if (!window.confirm(`⚠ IMPORT FORNITORI (${mode})\n\n${mode === "merge-by-piva" ? "Verranno aggiornati i fornitori esistenti matchando per P.IVA e aggiunti i nuovi." : "Verranno CANCELLATI tutti i fornitori esistenti e reimportati."}\n\nProcedere?`)) return;
                try {
                  const r = await api.post(`/pn/import/anagrafica?mode=${mode}`, { url: url.trim() }, { timeout: 180000 });
                  alert(`✓ Import FORNITORI OK (${r.data?.mode}):\n• ${r.data?.inserted || 0} nuovi\n• ${r.data?.updated || 0} aggiornati\n• ${r.data?.imported || 0} totali`);
                } catch (e) {
                  alert("✗ Errore import fornitori:\n" + (e.response?.data?.detail || e.message));
                }
              }}
              style={{ padding: "14px 16px", background: "#2e7d32", color: "white", border: "none", fontSize: 13, fontWeight: 800, cursor: "pointer", letterSpacing: 0.3 }}>
              ⬇ IMPORTA FOGLIO EXCEL FORNITORI
            </button>

            {/* 4) IMPORTA FOGLIO EXCEL PRIMA NOTA COMPLETA (upload diretto) */}
            <div data-testid="pn-maint-import-primanota-block" style={{ padding: 14, background: "#1b5e20", color: "white", border: "none", fontSize: 13, fontWeight: 800, letterSpacing: 0.3, display: "flex", flexDirection: "column", gap: 8 }}>
              <div>⬇ IMPORTA PRIMA NOTA COMPLETA (upload)</div>
              <input id="pn-up-pn" type="file" accept=".xlsx,.xls" data-testid="pn-up-pn" style={{ fontSize: 11, background: "white", color: "black", padding: 4 }} />
              <input id="pn-up-cr" type="file" accept=".xlsx,.xls" data-testid="pn-up-cr" style={{ fontSize: 11, background: "white", color: "black", padding: 4 }} />
              <button data-testid="pn-maint-import-primanota"
                onClick={async () => {
                  const fpn = document.getElementById("pn-up-pn")?.files?.[0];
                  const fcr = document.getElementById("pn-up-cr")?.files?.[0];
                  if (!fpn) { alert("Seleziona prima il file PN (Prima Nota)"); return; }
                  if (!fcr) { alert("Seleziona poi il file COSTI E RICAVI"); return; }
                  if (!window.confirm(`⚠ IMPORT PRIMA NOTA COMPLETA\n\n• PN: ${fpn.name} (${(fpn.size/1024).toFixed(1)} KB)\n• Costi/Ricavi: ${fcr.name} (${(fcr.size/1024).toFixed(1)} KB)\n\nVerranno CANCELLATE tutte le testate e righe esistenti.\n\nProcedere?`)) return;
                  try {
                    const fd = new FormData();
                    fd.append("pn_file", fpn);
                    fd.append("cr_file", fcr);
                    fd.append("overwrite", "true");
                    const r = await api.post("/pn/import/pn-completa-upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
                    alert(`✓ Import PRIMA NOTA OK:\n• Testate: ${r.data?.testate_imported || 0}\n• Righe contabili: ${r.data?.righe_imported || 0}\n• Saltate (testate): ${r.data?.skipped_testate || 0}\n• Saltate (righe): ${r.data?.skipped_righe || 0}`);
                  } catch (e) {
                    alert("✗ Errore import Prima Nota:\n" + (e.response?.data?.detail || e.message));
                  }
                }}
                style={{ padding: "10px 14px", background: "white", color: "#1b5e20", border: "none", fontWeight: 800, cursor: "pointer", fontSize: 12 }}>
                IMPORTA I 2 FILE
              </button>
            </div>

            {/* 5) CANCELLA COSTI E RICAVI */}
            <button data-testid="pn-maint-del-costi-ricavi"
              onClick={async () => {
                const conf = window.prompt(
                  "⚠ CANCELLAZIONE COSTI E RICAVI ⚠\n\n" +
                  "Verranno cancellate TUTTE le righe contabili (pn_costi_contabili).\n" +
                  "Le testate Prima Nota (fatture, note credito, extra) NON verranno toccate.\n\n" +
                  "Per confermare, digita esattamente la parola: CANCELLA"
                );
                if (conf !== "CANCELLA") { if (conf !== null) alert("Operazione annullata."); return; }
                try {
                  const r = await api.post("/pn/maintenance/reset-costi-ricavi", null, { params: { confirm: "CANCELLA" } });
                  alert(`✓ Cancellate ${r.data?.deleted_righe || 0} righe di Costi e Ricavi.`);
                } catch (e) {
                  alert("Errore: " + (e.response?.data?.detail || e.message));
                }
              }}
              style={{ padding: "14px 16px", background: "#7f0000", color: "white", border: "none", fontSize: 13, fontWeight: 800, cursor: "pointer", letterSpacing: 0.3 }}>
              🗑 CANCELLA COSTI E RICAVI
            </button>

            {/* 6) IMPORTA COSTI E RICAVI */}
            <button data-testid="pn-maint-import-costi-ricavi"
              onClick={async () => {
                const url = window.prompt(
                  "URL del file 'Costi e Ricavi.xlsx' (righe contabili):",
                  "https://customer-assets.emergentagent.com/job_data-extraction-tool-8/artifacts/d44en4m9_COSTI%20E%20RICAVI.xlsx"
                );
                if (!url || !url.trim()) return;
                if (!window.confirm("⚠ IMPORT COSTI E RICAVI\n\nVerranno CANCELLATE tutte le righe contabili esistenti e reimportate dal file Excel.\nLe testate Prima Nota NON verranno toccate.\n\nProcedere?")) return;
                try {
                  const r = await api.post("/pn/import/costi-ricavi", { url: url.trim() }, { timeout: 300000 });
                  alert(`✓ Import COSTI E RICAVI OK:\n• ${r.data?.imported || 0} righe contabili importate`);
                } catch (e) {
                  alert("✗ Errore import Costi e Ricavi:\n" + (e.response?.data?.detail || e.message));
                }
              }}
              style={{ padding: "14px 16px", background: "#2e7d32", color: "white", border: "none", fontSize: 13, fontWeight: 800, cursor: "pointer", letterSpacing: 0.3 }}>
              ⬇ IMPORTA COSTI E RICAVI
            </button>

            {/* 7) RICOLLEGA FATTURE VIA P.IVA */}
            <button data-testid="pn-maint-rilink-piva"
              onClick={async () => {
                if (!window.confirm("🔗 RICOLLEGA FATTURE AI FORNITORI (via P.IVA)\n\nPer ogni fattura orfana (id_cliente non esistente in anagrafica), cerca un fornitore corrente con la STESSA P.IVA negli snapshot di pn_anagrafica_dedupe_log e aggiorna il collegamento.\n\nProcedere?")) return;
                try {
                  const r = await api.post("/pn/anagrafica/rilink-fatture-by-piva");
                  const d = r.data || {};
                  alert(
                    `✓ Rilink completato:\n` +
                    `• id_cliente distinti: ${d.id_cliente_distinti}\n` +
                    `• Già collegati: ${d.gia_collegati}\n` +
                    `• Rilink eseguiti: ${d.rilink_eseguiti}\n` +
                    `• Fatture aggiornate: ${d.fatture_aggiornate}\n` +
                    `• No P.IVA nello storico: ${d.no_piva_nello_storico}\n` +
                    `• No fornitore con quella P.IVA: ${d.no_fornitore_con_quella_piva}`
                  );
                } catch (e) {
                  alert("✗ Errore rilink:\n" + (e.response?.data?.detail || e.message));
                }
              }}
              style={{ padding: "14px 16px", background: "#00838f", color: "white", border: "none", fontSize: 13, fontWeight: 800, cursor: "pointer", letterSpacing: 0.3 }}>
              🔗 RICOLLEGA FATTURE AI FORNITORI (via P.IVA)
            </button>
          </div>
        </div>
      </div>
      )}

      {activeTab === "test_portale" && (
        <div style={{ margin: "0 20px" }}>
          <div className="sw-card">
            <div className="sw-card-header">🧪 Test Portale Alunni</div>
            <div className="sw-card-body">
              <p style={{ fontSize: 13, color: "#444", lineHeight: 1.6, marginBottom: 14 }}>
                Crea un alunno fittizio nel database con <code>_test_alunno=true</code> per provare il flusso del Portale Alunni senza intaccare la lista alunni reale (sincronizzata da Segremat Web).
              </p>
              <button onClick={() => setTestAlunnoOpen(true)} data-testid="admin-test-alunno-btn"
                style={{ padding: "10px 20px", background: "#8e24aa", color: "white", border: "none", borderRadius: 3, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}>
                🧪 Crea Alunno di Test
              </button>
            </div>
          </div>
          {testAlunnoOpen && (
            <TestAlunnoModal
              defaultAnno={annoCorrente}
              onClose={() => setTestAlunnoOpen(false)}
              onCreated={() => { /* nessun reload necessario qui */ }}
            />
          )}
        </div>
      )}
    </div>
  );
}
